import { convertTimestampToDate } from "../utils/firestoreUtils.js";

export const ADMIN_TIERS = {
  SUPER_ADMIN: "super_admin", // Full system access
  ADMIN: "admin", // General admin access
  FINANCE: "finance",
  SUPPORT: "support", // Customer support access
};

export const ASSIGNABLE_ADMIN_TIERS = [
  ADMIN_TIERS.SUPER_ADMIN,
  ADMIN_TIERS.ADMIN,
  ADMIN_TIERS.FINANCE,
  ADMIN_TIERS.SUPPORT,
];

export const ADMIN_ROLE_DESCRIPTIONS = {
  [ADMIN_TIERS.SUPER_ADMIN]: "Full access, including assigning admin roles.",
  [ADMIN_TIERS.ADMIN]:
    "Manage users, portfolios, meetings, moderation and services; view analytics. Cannot manage finances or assign admin roles.",
  [ADMIN_TIERS.FINANCE]:
    "Manage payouts, refunds, fee waivers and billing campaigns; view users, meetings and moderation. Cannot manage users or assign admin roles.",
  [ADMIN_TIERS.SUPPORT]:
    "View users, meetings and services; resend meeting emails. Cannot edit users, change meeting status or assign roles.",
};

export const TIER_DEFAULT_PERMISSIONS = {
  [ADMIN_TIERS.SUPER_ADMIN]: ["all"],
  [ADMIN_TIERS.ADMIN]: [
    "users:view",
    "users:manage",
    "users:impersonate",
    "meetings:view",
    "meetings:manage",
    "meetings:delete",
    "meetings:resend_emails",
    "meetings:change_status",
    "moderation:view",
    "moderation:resolve",
    "moderation:assign",
    "services:view",
    "services:manage",
    "analytics:view",
  ],
  [ADMIN_TIERS.SUPPORT]: [
    "users:view",
    "meetings:view",
    "meetings:resend_emails",
    "services:view",
  ],
  [ADMIN_TIERS.FINANCE]: [
    "users:view",
    "meetings:view",
    "moderation:view",
    "finance:manage",
  ],
};

export const getRolePermissions = (role) => [
  ...(TIER_DEFAULT_PERMISSIONS[role] || []),
];

export function syncAdminRole(token, tier) {
  const role = tier?.admin_tier;
  delete token.admin_tier;
  delete token.permissions;
  if (tier) {
    token.tier = convertTimestampToDate(tier);
    if (!Object.values(ADMIN_TIERS).includes(role)) {
      delete token.tier.admin_tier;
    }
  } else {
    delete token.tier;
  }
  if (Object.values(ADMIN_TIERS).includes(role)) {
    token.admin_tier = role;
    token.permissions = getRolePermissions(role);
  }
  return token;
}

export function hasPermission(session, permission) {
  if (!session?.user || session.impersonating || session.user.isBlocked) {
    return false;
  }
  // Super admins have all permissions
  if (session?.user?.tier?.admin_tier === ADMIN_TIERS.SUPER_ADMIN) return true;
  if (session?.user?.permissions?.includes("all")) return true;
  // Check explicit permissions
  if (session?.user?.permissions?.includes(permission)) return true;
  // Check tier default permissions
  const tier = session?.user?.tier?.admin_tier;
  if (tier && TIER_DEFAULT_PERMISSIONS[tier]?.includes(permission)) return true;
  return false;
}

export function hasAdminRole(session) {
  return Boolean(
    !session?.impersonating &&
      !session?.user?.isBlocked &&
      Object.values(ADMIN_TIERS).includes(getAdminRole(session)),
  );
}

export function getAdminRole(session) {
  const role = session?.user?.tier?.admin_tier;
  return Object.values(ADMIN_TIERS).includes(role) ? role : null;
}

/**
 * Common permission check functions
 */
export function canManageUsers(session) {
  return hasPermission(session, "users:manage");
}

export function canManageFinance(session) {
  return hasPermission(session, "finance:manage");
}

export function canManageUserAccount(session, user) {
  return (
    canManageUsers(session) &&
    (!user?.tier?.admin_tier || isSuperAdmin(session))
  );
}

export function canViewUsers(session) {
  return (
    hasPermission(session, "users:view") ||
    hasPermission(session, "users:manage")
  );
}

export function canImpersonateUsers(session) {
  return hasPermission(session, "users:impersonate");
}

export function canManageSupport(session) {
  return hasPermission(session, "support:manage");
}

export function canConfigureSystem(session) {
  return hasPermission(session, "system:configure");
}

/**
 * Meeting management permission functions
 */
export function canViewMeetings(session) {
  return hasPermission(session, "meetings:view");
}

export function canManageMeetings(session) {
  return hasPermission(session, "meetings:manage");
}

export function canResendMeetingEmails(session) {
  return hasPermission(session, "meetings:resend_emails");
}

export function canDeleteMeetings(session) {
  return hasPermission(session, "meetings:delete");
}

export function canChangeMeetingStatus(session) {
  return hasPermission(session, "meetings:change_status");
}

export function isSuperAdmin(session) {
  return (
    !session?.impersonating &&
    !session?.user?.isBlocked &&
    session?.user?.tier?.admin_tier === ADMIN_TIERS.SUPER_ADMIN
  );
}

/**
 * Moderation permission functions
 */
export function canViewModeration(session) {
  return hasPermission(session, "moderation:view");
}

export function canViewServices(session) {
  return hasPermission(session, "services:view");
}

export function canManageServices(session) {
  return hasPermission(session, "services:manage");
}

export function canResolveModeration(session) {
  return hasPermission(session, "moderation:resolve");
}

/**
 * Analytics dashboard (PostHog KPIs + embedded dashboards).
 * Available to super admins and general admins by default.
 */
export function canViewAnalytics(session) {
  return hasPermission(session, "analytics:view");
}

export function canAssignModeration(session) {
  return hasPermission(session, "moderation:assign");
}

export function canManageModeration(session) {
  return hasPermission(session, "moderation:manage");
}

export function canApproveMeetingEmails(session) {
  return (
    hasPermission(session, "moderation:resolve") ||
    hasPermission(session, "moderation:manage")
  );
}
